import { useState, useEffect, useRef } from 'react';
import '@/App.css';
import '@/AwardFx.css';
import { Mail, Phone, Video, Film, Tv, Users, CheckCircle, ArrowRight, Play, Facebook, Instagram, ChevronDown, MapPin, Sparkles, Star, ExternalLink, MessageCircle } from 'lucide-react';
import CookieBanner from '@/CookieBanner';
import WhatsAppButton from '@/WhatsAppButton';
import ContactForm from '@/ContactForm';
import VideoWall from '@/VideoWall';
import TrustBar from '@/TrustBar';
import UseCases from '@/UseCases';
import Pricing from '@/Pricing';
import { KiWerbespot, KiProduktvideo, KiMusikvideo } from '@/ServicePages';
import { RadarList, RadarPost, RadarAdmin } from '@/RadarPages';
import RadarTeaser from '@/RadarTeaser';
import CustomCursor from '@/CustomCursor';
import ScrollProgress from '@/ScrollProgress';
import { BrowserRouter, Routes, Route, Link, useLocation } from 'react-router-dom';
import { Datenschutz, Impressum } from '@/LegalPages';

const faqData = [
    {
      q: "Was ist AI Videoproduktion?",
      a: "AI Videoproduktion nutzt künstliche Intelligenz (KI), um innovative Videoclips zu erstellen. Bei mAIsblau in Wien kombinieren wir modernste KI-Tools mit professioneller Filmproduktion, um schnelle, kreative und kosteneffiziente Videos für Social Media, Werbung und Marketing zu produzieren."
    },
    {
      q: "Sehen KI-Videos unnatürlich oder „robotisch“ aus?",
      a: "Nein – wenn Filmprofis am Werk sind. Unser Team kommt aus klassischer Kamera-, Schnitt- und Color-Arbeit und veredelt jedes KI-Video mit Human-in-the-loop-Feinschliff. So vermeiden wir den typischen KI-Look. Hybrid-Produktionen (echter Dreh + KI-Erweiterung) liefern oft die natürlichste Wirkung."
    },
    {
      q: "Was kostet eine AI Videoproduktion in Wien?",
      a: "Die Kosten variieren je nach Projektumfang. KI-generierte Kurzvideos für Social Media starten bereits ab wenigen hundert Euro. Klassische Produktionen wie Imagefilme oder Dokumentationen werden individuell kalkuliert. Kontaktieren Sie uns für ein kostenloses Erstgespräch!"
    },
    {
      q: "Wie viel kostet eine KI-Videoproduktion im Vergleich zum klassischen Dreh?",
      a: "Deutlich günstiger und planbarer – besonders bei Varianten und Social-Clips, weil Location, Crew, Casting und Drehtage entfallen. Echte Drehs bleiben sinnvoll, wenn Authentizität (Mitarbeiter, Standort, Produkt in der Hand) im Mittelpunkt steht. Wir kalkulieren transparent und kombinieren beides."
    },
    {
      q: "Wie schnell geht eine KI-Videoproduktion?",
      a: "Erste Ergebnisse liefern wir häufig in wenigen Werktagen. Klassische Produktionen dauern oft 4–8 Wochen, KI-gestützte meist nur Tage bis 1–2 Wochen. Bei Hybrid-Projekten planen wir den realen Dreh effizient und erweitern generativ – ideal für schnelle Kampagnen."
    },
    {
      q: "Kann man echten Dreh und KI kombinieren (Hybrid)?",
      a: "Ja – und oft ist das die beste Lösung. Wir drehen echte Menschen, Produkte und Locations in Wien und erweitern generativ: Set-Extensions, Hintergründe, zusätzliche Formate. Authentizität plus das Unmögliche in einem Film."
    },
    {
      q: "Was ist der Unterschied zwischen Voll-KI und Hybrid?",
      a: "Voll-KI: kein Dreh nötig, maximal schnell und flexibel. Hybrid: echte Aufnahmen plus generative Erweiterung – mehr Authentizität und oft die stärkste Lösung."
    },
    {
      q: "Kann mein echtes Produkt realistisch und konsistent vorkommen?",
      a: "Ja. Wir arbeiten mit echten Produktfotos oder kurzen Drehs und halten Form, Farbe, Logo und Material konsistent über alle Szenen. Bei Bedarf drehen wir das Produkt real und ergänzen die Welt mit KI."
    },
    {
      q: "Kann man Kamerabewegungen und Stil präzise steuern?",
      a: "Ja. Mit Referenzbildern und -videos sowie gezielter Steuerung erreichen wir filmische Kameraführung – besonders wichtig für konsistente Kampagnen."
    },
    {
      q: "Ist die Qualität gut genug für Marken und Werbung?",
      a: "Ja – wenn Idee, Bildsprache, Licht, Schnitt und Farbe von Filmemachern kommen. Reine Prompt-Generierung reicht selten für Markenqualität."
    },
    {
      q: "Welche Videos eignen sich besonders für KI?",
      a: "Produktreels, Social-Clips (Reels/Shorts), Werbespots, Konzept-Visualisierungen, Musikvideos und Erklärfilme. Weniger ideal, wenn echte Mitarbeiter, echte Emotionen vor Ort oder der Standort selbst die Story sind."
    },
    {
      q: "Eignet sich KI auch für erzählende Filme oder Musikvideos?",
      a: "Ja. Generierte Welten plus handwerklicher Schnitt, Pacing und Sounddesign funktionieren sehr gut für narrative Formate – hören und sehen Sie sich zum Beispiel unser Musikvideo „Notfallsong“ an."
    },
    {
      q: "Eignet sich das für Social Media (Reels, TikTok, LinkedIn)?",
      a: "Sehr gut. Wir liefern direkt die richtigen Formate (9:16, 1:1, 16:9) und Cutdowns für alle Kanäle."
    },
    {
      q: "Wann ist klassische Produktion besser als KI?",
      a: "Wenn echte Menschen, echte Emotionen, der reale Standort oder ein physisches Produkt im Mittelpunkt stehen sollen. Wir beraten ehrlich und mischen beides, wo es Sinn macht."
    },
    {
      q: "Wie läuft eine KI-Videoproduktion ab?",
      a: "Briefing → Konzept/Storyboard → Look-Entwicklung → Umsetzung (KI oder Hybrid) → Postproduktion mit Schnitt, Color und Sound (DaVinci o. Ä.) → fertige Formate für alle Kanäle. Erste Ergebnisse sehen Sie oft schon nach wenigen Werktagen."
    },
    {
      q: "Was passiert, wenn der erste Entwurf nicht passt?",
      a: "Wir arbeiten iterativ mit Korrekturschleifen. Änderungen gehen bei KI deutlich schneller und günstiger als Nachdrehs."
    },
    {
      q: "Brauche ich ein fertiges Skript?",
      a: "Nein. Wir entwickeln Konzept und Text gemeinsam aus Ihrem Briefing."
    },
    {
      q: "Welche Tools werden verwendet?",
      a: "Aktuelle KI-Modelle der neuesten Generation plus klassische Postproduktion. Entscheidend ist nicht das Tool, sondern die filmische Regie und der Feinschliff."
    },
    {
      q: "Wer besitzt die Rechte am fertigen Video?",
      a: "Mit Abnahme und Bezahlung gehen die kommerziellen Nutzungsrechte auf Sie über – für Website, Social Media, Ads und mehr. Transparent geregelt."
    },
    {
      q: "Müssen KI-Videos gemäß EU AI Act gekennzeichnet werden?",
      a: "Ja – seit 2. August 2026 besonders relevant bei Deepfakes oder täuschend echten Inhalten. Wir kennzeichnen transparent nach Artikel 50 (sichtbar und ggf. maschinenlesbar) und beraten Sie, wann genau die Pflicht greift und wie man sie stilvoll umsetzt, ohne den Film zu stören."
    },
    {
      q: "Ist KI-Videoproduktion DSGVO-konform?",
      a: "Ja. Wir achten auf EU-konforme Prozesse und vermeiden unnötige personenbezogene Daten. Bei Avataren oder Stimmen klären wir die Rechtsgrundlage im Vorfeld."
    },
    {
      q: "Gibt es lokale KI-Video-Anbieter in Wien?",
      a: "Ja. Als Wiener Produktion kennen wir den Markt, die Locations und die Erwartungen österreichischer Kunden – wir arbeiten vor Ort oder remote."
    },
    {
      q: "Welche Regionen rund um Wien bedient mAIsblau?",
      a: "Wir bedienen Wien und einen Umkreis von 100km. Das umfasst ganz Niederösterreich (St. Pölten, Wiener Neustadt, Baden, Mödling, Krems, Klosterneuburg, Tulln), das nördliche Burgenland (Eisenstadt, Neusiedl am See) sowie Bratislava."
    },
    {
      q: "Warum AI Videos für mein Unternehmen in Wien?",
      a: "AI Videos bieten entscheidende Vorteile: schnellere Produktionszeiten, geringere Kosten als traditionelle Drehs, unbegrenzte kreative Möglichkeiten und die Fähigkeit, einzigartige visuelle Welten zu erschaffen. Ideal für Social Media Marketing, Werbung und Markenaufbau in Wien."
    },
    {
      q: "Bietet mAIsblau auch klassische Videoproduktion an?",
      a: "Ja! Neben AI Videoproduktion bieten wir klassische Filmproduktion: Interviews, Videoportraits, TV-Produktionen, Dokumentationen, Imagefilme und Event-Dokumentation. Unser journalistischer Zugang macht jede Produktion besonders authentisch."
    }
  ];

function HomePage() {
  const [selectedVideo, setSelectedVideo] = useState(null);
  const [openFaq, setOpenFaq] = useState(null);
  const [mapVisible, setMapVisible] = useState(false);
  const mapRef = useRef(null);

  useEffect(() => {
    document.title = 'AI Videoproduktion Wien | KI Videos & Filmproduktion | mAIsblau';
    document.querySelector('meta[name="description"]')?.setAttribute('content', 'AI Videoproduktion Wien: KI-generierte Videos, Imagefilme, Werbespots & Dokumentationen. mAIsblau produziert innovative AI Videos und klassische Filmproduktion in Wien und Umgebung (100km). Jetzt Projekt starten!');
    if (window.location.hash === '#contact') {
      setTimeout(() => document.getElementById('contact')?.scrollIntoView({ behavior: 'auto' }), 300);
    }
  }, []);

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') setSelectedVideo(null); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  useEffect(() => {
    if (!mapRef.current) return;
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) { setMapVisible(true); observer.disconnect(); } },
      { rootMargin: '200px' }
    );
    observer.observe(mapRef.current);
    return () => observer.disconnect();
  }, []);

  // Scroll reveal animations (IntersectionObserver + scroll fallback for
  // mobile content-visibility sections whose IO callbacks can be delayed)
  useEffect(() => {
    const els = Array.from(document.querySelectorAll('[data-reveal]'));
    const pending = new Set(els);
    const reveal = (el) => {
      el.classList.add('revealed');
      pending.delete(el);
    };
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            reveal(entry.target);
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0, rootMargin: '0px 0px 150px 0px' }
    );
    els.forEach((el) => observer.observe(el));

    let raf = null;
    const sweep = () => {
      raf = null;
      const limit = window.innerHeight + 150;
      pending.forEach((el) => {
        if (el.getBoundingClientRect().top < limit) reveal(el);
      });
      if (pending.size === 0) window.removeEventListener('scroll', onScroll);
    };
    const onScroll = () => {
      if (!raf) raf = requestAnimationFrame(sweep);
    };
    window.addEventListener('scroll', onScroll, { passive: true });

    return () => {
      observer.disconnect();
      window.removeEventListener('scroll', onScroll);
      if (raf) cancelAnimationFrame(raf);
    };
  }, []);

  const scrollToContact = () => {
    document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <div className="homepage-wrapper">
      {/* Energy Vine - Decorative animated nebula spiral */}
      <div className="energy-vine" aria-hidden="true">
        <svg className="vine-svg" viewBox="0 0 200 6000" preserveAspectRatio="none" xmlns="http://www.w3.org/2000/svg">
          <defs>
            <linearGradient id="vineGrad" x1="0%" y1="0%" x2="0%" y2="100%">
              <stop offset="0%" stopColor="#00e0ff" stopOpacity="0.9" />
              <stop offset="12%" stopColor="#7c3aed" stopOpacity="0.7" />
              <stop offset="25%" stopColor="#00e0ff" stopOpacity="0.85" />
              <stop offset="37%" stopColor="#a855f7" stopOpacity="0.6" />
              <stop offset="50%" stopColor="#00e0ff" stopOpacity="0.9" />
              <stop offset="62%" stopColor="#8b5cf6" stopOpacity="0.7" />
              <stop offset="75%" stopColor="#00e0ff" stopOpacity="0.85" />
              <stop offset="87%" stopColor="#7c3aed" stopOpacity="0.6" />
              <stop offset="100%" stopColor="#00e0ff" stopOpacity="0.8" />
            </linearGradient>
            <linearGradient id="tendrilGrad1" x1="0%" y1="0%" x2="100%" y2="0%">
              <stop offset="0%" stopColor="#00e0ff" stopOpacity="0.6" />
              <stop offset="100%" stopColor="#00e0ff" stopOpacity="0" />
            </linearGradient>
            <linearGradient id="tendrilGrad2" x1="100%" y1="0%" x2="0%" y2="0%">
              <stop offset="0%" stopColor="#7c3aed" stopOpacity="0.5" />
              <stop offset="100%" stopColor="#a855f7" stopOpacity="0" />
            </linearGradient>
            <filter id="vineGlow" x="-80%" y="-2%" width="260%" height="104%">
              <feGaussianBlur in="SourceGraphic" stdDeviation="5" result="blur1" />
              <feGaussianBlur in="SourceGraphic" stdDeviation="2" result="blur2" />
              <feMerge>
                <feMergeNode in="blur1" />
                <feMergeNode in="blur2" />
                <feMergeNode in="SourceGraphic" />
              </feMerge>
            </filter>
            <filter id="vineGlowWide" x="-200%" y="-2%" width="500%" height="104%">
              <feGaussianBlur in="SourceGraphic" stdDeviation="35" />
            </filter>
            <filter id="vineGlowMedium" x="-150%" y="-2%" width="400%" height="104%">
              <feGaussianBlur in="SourceGraphic" stdDeviation="18" />
            </filter>
            <filter id="tendrilGlow" x="-50%" y="-50%" width="200%" height="200%">
              <feGaussianBlur in="SourceGraphic" stdDeviation="4" result="blur1" />
              <feGaussianBlur in="SourceGraphic" stdDeviation="1.5" result="blur2" />
              <feMerge>
                <feMergeNode in="blur1" />
                <feMergeNode in="blur2" />
                <feMergeNode in="SourceGraphic" />
              </feMerge>
            </filter>
          </defs>
          
          {/* Ultra-wide atmospheric nebula */}
          <path 
            className="vine-path-atmosphere"
            d="M 100,0 C 60,150 40,300 30,500 C 20,700 60,850 100,1000 C 140,1150 170,1300 160,1500 C 150,1700 100,1850 60,2000 C 20,2150 10,2300 30,2500 C 50,2700 100,2850 140,3000 C 180,3150 170,3350 140,3500 C 110,3650 60,3800 40,4000 C 20,4200 50,4400 90,4500 C 130,4600 160,4800 150,5000 C 140,5200 90,5400 60,5500 C 30,5600 60,5800 100,6000"
            fill="none"
            stroke="url(#vineGrad)"
            strokeWidth="70"
            filter="url(#vineGlowWide)"
            opacity="0.35"
          />
          
          {/* Medium diffuse glow */}
          <path 
            className="vine-path-glow"
            d="M 100,0 C 60,150 40,300 30,500 C 20,700 60,850 100,1000 C 140,1150 170,1300 160,1500 C 150,1700 100,1850 60,2000 C 20,2150 10,2300 30,2500 C 50,2700 100,2850 140,3000 C 180,3150 170,3350 140,3500 C 110,3650 60,3800 40,4000 C 20,4200 50,4400 90,4500 C 130,4600 160,4800 150,5000 C 140,5200 90,5400 60,5500 C 30,5600 60,5800 100,6000"
            fill="none"
            stroke="url(#vineGrad)"
            strokeWidth="25"
            filter="url(#vineGlowMedium)"
            opacity="0.55"
          />
          
          {/* Main vine stem */}
          <path 
            className="vine-path-main"
            d="M 100,0 C 60,150 40,300 30,500 C 20,700 60,850 100,1000 C 140,1150 170,1300 160,1500 C 150,1700 100,1850 60,2000 C 20,2150 10,2300 30,2500 C 50,2700 100,2850 140,3000 C 180,3150 170,3350 140,3500 C 110,3650 60,3800 40,4000 C 20,4200 50,4400 90,4500 C 130,4600 160,4800 150,5000 C 140,5200 90,5400 60,5500 C 30,5600 60,5800 100,6000"
            fill="none"
            stroke="url(#vineGrad)"
            strokeWidth="4"
            filter="url(#vineGlow)"
            opacity="0.8"
          />
          
          {/* Bright core */}
          <path 
            className="vine-path-core"
            d="M 100,0 C 60,150 40,300 30,500 C 20,700 60,850 100,1000 C 140,1150 170,1300 160,1500 C 150,1700 100,1850 60,2000 C 20,2150 10,2300 30,2500 C 50,2700 100,2850 140,3000 C 180,3150 170,3350 140,3500 C 110,3650 60,3800 40,4000 C 20,4200 50,4400 90,4500 C 130,4600 160,4800 150,5000 C 140,5200 90,5400 60,5500 C 30,5600 60,5800 100,6000"
            fill="none"
            stroke="#00e0ff"
            strokeWidth="1.5"
            opacity="0.5"
          />
          
          {/* === TENDRILS - Fine curly spiral threads === */}
          
          {/* Tendril cluster 1 - around y:300 */}
          <path d="M 38,300 C 55,280 75,260 90,250 C 105,240 125,245 130,260 C 135,275 120,285 110,280" fill="none" stroke="url(#tendrilGrad1)" strokeWidth="1.2" filter="url(#tendrilGlow)" opacity="0.5" className="vine-tendril" />
          <path d="M 35,350 C 25,330 15,310 5,300 C -5,290 -10,300 -5,310 C 0,320 10,315 8,310" fill="none" stroke="url(#tendrilGrad2)" strokeWidth="1" filter="url(#tendrilGlow)" opacity="0.4" className="vine-tendril" />
          <path d="M 42,280 C 60,270 80,275 95,285 C 108,295 105,310 95,308" fill="none" stroke="#00e0ff" strokeWidth="0.8" filter="url(#tendrilGlow)" opacity="0.35" className="vine-tendril" />
          
          {/* Tendril cluster 2 - around y:700 */}
          <path d="M 30,700 C 10,680 -5,660 -15,650 C -25,640 -20,655 -10,665 C 0,675 -5,670 -8,665" fill="none" stroke="url(#tendrilGrad2)" strokeWidth="1.2" filter="url(#tendrilGlow)" opacity="0.5" className="vine-tendril" />
          <path d="M 40,750 C 60,740 85,730 100,720 C 115,710 120,720 112,728 C 104,736 110,732 115,725" fill="none" stroke="url(#tendrilGrad1)" strokeWidth="1" filter="url(#tendrilGlow)" opacity="0.4" className="vine-tendril" />
          
          {/* Tendril cluster 3 - around y:1100 */}
          <path d="M 115,1100 C 140,1080 160,1065 175,1060 C 190,1055 195,1070 185,1078 C 175,1086 180,1080 188,1073" fill="none" stroke="url(#tendrilGrad1)" strokeWidth="1.2" filter="url(#tendrilGlow)" opacity="0.5" className="vine-tendril" />
          <path d="M 120,1150 C 100,1135 80,1125 65,1120 C 50,1115 48,1128 58,1133 C 68,1138 60,1133 52,1128" fill="none" stroke="url(#tendrilGrad2)" strokeWidth="1" filter="url(#tendrilGlow)" opacity="0.4" className="vine-tendril" />
          <path d="M 110,1080 C 130,1070 150,1075 165,1085 C 178,1095 172,1105 162,1100" fill="none" stroke="#7c3aed" strokeWidth="0.8" filter="url(#tendrilGlow)" opacity="0.3" className="vine-tendril" />
          
          {/* Tendril cluster 4 - around y:1500 */}
          <path d="M 160,1500 C 180,1480 195,1465 205,1460 C 215,1455 210,1470 200,1475 C 190,1480 195,1473 203,1468" fill="none" stroke="url(#tendrilGrad1)" strokeWidth="1.2" filter="url(#tendrilGlow)" opacity="0.45" className="vine-tendril" />
          <path d="M 155,1550 C 135,1535 115,1520 100,1515 C 85,1510 85,1525 95,1528 C 105,1531 98,1526 88,1520" fill="none" stroke="url(#tendrilGrad2)" strokeWidth="1" filter="url(#tendrilGlow)" opacity="0.4" className="vine-tendril" />
          
          {/* Tendril cluster 5 - around y:1900 */}
          <path d="M 70,1900 C 50,1885 30,1870 18,1862 C 6,1854 8,1868 18,1873 C 28,1878 20,1872 10,1866" fill="none" stroke="url(#tendrilGrad2)" strokeWidth="1.2" filter="url(#tendrilGlow)" opacity="0.5" className="vine-tendril" />
          <path d="M 75,1950 C 95,1940 120,1935 138,1932 C 156,1929 155,1942 145,1945 C 135,1948 140,1942 150,1936" fill="none" stroke="url(#tendrilGrad1)" strokeWidth="1" filter="url(#tendrilGlow)" opacity="0.4" className="vine-tendril" />
          <path d="M 65,1880 C 45,1870 28,1875 18,1885 C 10,1895 18,1900 25,1895" fill="none" stroke="#00e0ff" strokeWidth="0.8" filter="url(#tendrilGlow)" opacity="0.3" className="vine-tendril" />
          
          {/* Tendril cluster 6 - around y:2300 */}
          <path d="M 20,2300 C 0,2280 -15,2265 -22,2258 C -29,2251 -25,2265 -15,2270 C -5,2275 -10,2268 -20,2262" fill="none" stroke="url(#tendrilGrad2)" strokeWidth="1.2" filter="url(#tendrilGlow)" opacity="0.5" className="vine-tendril" />
          <path d="M 25,2350 C 45,2340 68,2332 82,2328 C 96,2324 95,2338 85,2340 C 75,2342 80,2338 90,2332" fill="none" stroke="url(#tendrilGrad1)" strokeWidth="1" filter="url(#tendrilGlow)" opacity="0.4" className="vine-tendril" />
          
          {/* Tendril cluster 7 - around y:2700 */}
          <path d="M 75,2700 C 95,2685 118,2675 135,2670 C 152,2665 150,2680 140,2683 C 130,2686 135,2680 145,2674" fill="none" stroke="url(#tendrilGrad1)" strokeWidth="1.2" filter="url(#tendrilGlow)" opacity="0.5" className="vine-tendril" />
          <path d="M 68,2750 C 48,2738 28,2728 15,2722 C 2,2716 5,2730 15,2733 C 25,2736 18,2730 8,2724" fill="none" stroke="url(#tendrilGrad2)" strokeWidth="1" filter="url(#tendrilGlow)" opacity="0.4" className="vine-tendril" />
          
          {/* Tendril cluster 8 - around y:3100 */}
          <path d="M 155,3100 C 175,3085 192,3072 202,3068 C 212,3064 208,3078 198,3082 C 188,3086 193,3080 203,3074" fill="none" stroke="url(#tendrilGrad1)" strokeWidth="1.2" filter="url(#tendrilGlow)" opacity="0.45" className="vine-tendril" />
          <path d="M 150,3150 C 130,3138 108,3128 92,3124 C 76,3120 78,3134 88,3138 C 98,3142 90,3136 80,3130" fill="none" stroke="url(#tendrilGrad2)" strokeWidth="1" filter="url(#tendrilGlow)" opacity="0.4" className="vine-tendril" />
          <path d="M 160,3080 C 178,3070 192,3078 200,3090 C 206,3100 198,3105 190,3098" fill="none" stroke="#a855f7" strokeWidth="0.8" filter="url(#tendrilGlow)" opacity="0.3" className="vine-tendril" />
          
          {/* Tendril cluster 9 - around y:3500 */}
          <path d="M 140,3500 C 160,3488 178,3480 190,3475 C 202,3470 198,3484 188,3488 C 178,3492 183,3486 193,3480" fill="none" stroke="url(#tendrilGrad1)" strokeWidth="1.2" filter="url(#tendrilGlow)" opacity="0.5" className="vine-tendril" />
          <path d="M 135,3550 C 115,3538 92,3528 78,3524 C 64,3520 66,3534 76,3538 C 86,3542 78,3536 68,3530" fill="none" stroke="url(#tendrilGrad2)" strokeWidth="1" filter="url(#tendrilGlow)" opacity="0.4" className="vine-tendril" />
          
          {/* Tendril cluster 10 - around y:3900 */}
          <path d="M 50,3900 C 30,3885 12,3872 2,3868 C -8,3864 -5,3878 5,3882 C 15,3886 8,3880 -2,3874" fill="none" stroke="url(#tendrilGrad2)" strokeWidth="1.2" filter="url(#tendrilGlow)" opacity="0.5" className="vine-tendril" />
          <path d="M 55,3950 C 75,3940 98,3932 112,3928 C 126,3924 125,3938 115,3942 C 105,3946 110,3940 120,3934" fill="none" stroke="url(#tendrilGrad1)" strokeWidth="1" filter="url(#tendrilGlow)" opacity="0.4" className="vine-tendril" />
          <path d="M 45,3880 C 28,3870 15,3878 8,3888 C 2,3898 10,3902 18,3896" fill="none" stroke="#00e0ff" strokeWidth="0.8" filter="url(#tendrilGlow)" opacity="0.3" className="vine-tendril" />
          
          {/* Tendril cluster 11 - around y:4300 */}
          <path d="M 70,4300 C 90,4288 112,4278 128,4274 C 144,4270 142,4284 132,4288 C 122,4292 127,4286 137,4280" fill="none" stroke="url(#tendrilGrad1)" strokeWidth="1.2" filter="url(#tendrilGlow)" opacity="0.45" className="vine-tendril" />
          <path d="M 62,4350 C 42,4335 22,4325 8,4320 C -6,4315 -2,4330 8,4333 C 18,4336 10,4330 0,4324" fill="none" stroke="url(#tendrilGrad2)" strokeWidth="1" filter="url(#tendrilGlow)" opacity="0.4" className="vine-tendril" />
          
          {/* Tendril cluster 12 - around y:4700 */}
          <path d="M 140,4700 C 160,4688 180,4678 192,4672 C 204,4666 200,4682 190,4685 C 180,4688 185,4683 195,4676" fill="none" stroke="url(#tendrilGrad1)" strokeWidth="1.2" filter="url(#tendrilGlow)" opacity="0.5" className="vine-tendril" />
          <path d="M 135,4750 C 115,4738 95,4730 80,4726 C 65,4722 68,4736 78,4740 C 88,4744 80,4738 70,4732" fill="none" stroke="url(#tendrilGrad2)" strokeWidth="1" filter="url(#tendrilGlow)" opacity="0.4" className="vine-tendril" />
          
          {/* Tendril cluster 13 - around y:5100 */}
          <path d="M 115,5100 C 95,5085 75,5072 62,5068 C 49,5064 52,5078 62,5082 C 72,5086 65,5080 55,5074" fill="none" stroke="url(#tendrilGrad2)" strokeWidth="1.2" filter="url(#tendrilGlow)" opacity="0.5" className="vine-tendril" />
          <path d="M 120,5150 C 142,5142 162,5138 175,5135 C 188,5132 185,5146 175,5148 C 165,5150 170,5145 180,5140" fill="none" stroke="url(#tendrilGrad1)" strokeWidth="1" filter="url(#tendrilGlow)" opacity="0.4" className="vine-tendril" />
          <path d="M 108,5080 C 90,5072 75,5078 65,5088 C 58,5098 65,5103 72,5096" fill="none" stroke="#7c3aed" strokeWidth="0.8" filter="url(#tendrilGlow)" opacity="0.3" className="vine-tendril" />
          
          {/* Tendril cluster 14 - around y:5600 */}
          <path d="M 55,5600 C 35,5585 18,5572 8,5568 C -2,5564 0,5578 10,5582 C 20,5586 14,5580 4,5574" fill="none" stroke="url(#tendrilGrad2)" strokeWidth="1.2" filter="url(#tendrilGlow)" opacity="0.45" className="vine-tendril" />
          <path d="M 60,5650 C 80,5642 102,5638 118,5635 C 134,5632 132,5646 122,5648 C 112,5650 117,5645 127,5640" fill="none" stroke="url(#tendrilGrad1)" strokeWidth="1" filter="url(#tendrilGlow)" opacity="0.4" className="vine-tendril" />
        </svg>
        {/* Floating nebula orbs along the vine */}
        <div className="vine-orb vine-orb-1"></div>
        <div className="vine-orb vine-orb-2"></div>
        <div className="vine-orb vine-orb-3"></div>
        <div className="vine-orb vine-orb-4"></div>
        <div className="vine-orb vine-orb-5"></div>
        <div className="vine-orb vine-orb-6"></div>
        <div className="vine-orb vine-orb-7"></div>
        <div className="vine-orb vine-orb-8"></div>
      </div>
      {/* Hero Section */}
      <section className="hero-section" data-testid="hero-section">
        <Link to="/ki-news" className="hero-news" data-magnetic="true" data-testid="hero-news-button" aria-label="KI-News Radar öffnen">
          <Sparkles size={16} /> KI-News
        </Link>
        <a
          href="https://wa.me/4368120179697?text=Hallo%20Andreas!%20Ich%20interessiere%20mich%20f%C3%BCr%20eine%20Videoproduktion."
          target="_blank"
          rel="noopener noreferrer"
          className="hero-whatsapp"
          data-magnetic="true"
          data-testid="hero-whatsapp-button"
          aria-label="WhatsApp Chat starten"
        >
          <MessageCircle size={18} /> WhatsApp
        </a>
        <div className="hero-overlay"></div>
        <div 
          className="hero-background"
          style={{
            backgroundImage: 'url(https://images.unsplash.com/photo-1576280314550-773c50583407?w=800&q=75)',
          }}
        ></div>
        <div className="hero-content">
          <div className="hero-logo" data-testid="hero-logo">
            <span className="logo-text">
              M<span className="ai-highlight">AI</span>SBLAU
            </span>
            <span className="logo-subtitle">Videoproduktion Wien</span>
          </div>
          <h1 className="hero-title" data-testid="hero-title">
            <span className="hero-word">Time</span>{' '}
            <span className="hero-word">to</span>{' '}
            <span className="hero-word">be</span>{' '}
            <span className="hero-word highlight">VISIBLE</span>
          </h1>
          <p className="hero-subtitle" data-testid="hero-subtitle">
            AI Videoproduktion & Filmproduktion für Menschen, Marken und Projekte in Wien
          </p>
          <button 
            onClick={scrollToContact} 
            className="cta-button"
            data-magnetic="true"
            data-testid="hero-cta-button"
          >
            Jetzt Projekt starten <ArrowRight className="ml-2" size={20} />
          </button>
        </div>
      </section>

      {/* Marquee Brand Strip */}
      <div className="marquee-strip" aria-hidden="true" data-testid="marquee-strip">
        <div className="marquee-track">
          {[0, 1].map((n) => (
            <div className="marquee-content" key={n}>
              <span>AI Videoproduktion</span><span className="marquee-dot">✦</span>
              <span>Filmproduktion Wien</span><span className="marquee-dot">✦</span>
              <span>Werbespots</span><span className="marquee-dot">✦</span>
              <span>Dokumentationen</span><span className="marquee-dot">✦</span>
              <span>TV-Produktionen</span><span className="marquee-dot">✦</span>
              <span>Time to be visible</span><span className="marquee-dot">✦</span>
            </div>
          ))}
        </div>
      </div>

      <VideoWall onPlay={setSelectedVideo} />

      <TrustBar />

      <UseCases />

      <RadarTeaser />

      {/* Services Section */}
      <section className="services-section" data-testid="services-section">
        <div className="container">
          <h2 className="section-title" data-testid="services-title" data-reveal>Unsere Leistungen</h2>
          <p className="section-subtitle" data-reveal>Authentische Unternehmensvideos & KI-gestützte Filmproduktion in Wien</p>
          
          <div className="services-grid" data-reveal="stagger">
            <div className="service-card ai-service-card" data-testid="service-ai">
              <div className="service-icon ai-icon-glow">
                <span className="ai-badge-service">AI</span>
              </div>
              <h3>AI-Videoproduktion</h3>
              <p>Innovative KI-generierte Videos für Social Media, Werbung und Marketing – schnell, modern und kosteneffizient.</p>
            </div>

            <div className="service-card" data-testid="service-interviews">
              <div className="service-icon">
                <Users size={40} />
              </div>
              <h3>Interviews & Videoportraits</h3>
              <p>Podcast-Style Interviews, die Geschichten erzählen und Menschen authentisch präsentieren.</p>
            </div>
            
            <div className="service-card" data-testid="service-commercials">
              <div className="service-icon">
                <Video size={40} />
              </div>
              <h3>Werbespots & Imagefilme</h3>
              <p>Kreative Werbefilme, die Ihre Marke emotional und visuell stark präsentieren.</p>
            </div>
            
            <div className="service-card" data-testid="service-documentaries">
              <div className="service-icon">
                <Film size={40} />
              </div>
              <h3>Dokumentationen</h3>
              <p>Tiefgründige Dokumentarfilme über wichtige Themen und gesellschaftliche Entwicklungen.</p>
            </div>
            
            <div className="service-card" data-testid="service-tv">
              <div className="service-icon">
                <Tv size={40} />
              </div>
              <h3>TV-Produktionen</h3>
              <p>Professionelle TV-Formate und Sendungen für österreichische und internationale Sender.</p>
            </div>
          </div>
        </div>
      </section>

      <Pricing />

      {/* About Section */}
      <section className="about-section" data-testid="about-section">
        <div className="container">
          <div className="about-grid">
            <div className="about-image" data-reveal style={{
              backgroundImage: 'url(https://customer-assets.emergentagent.com/job_vienna-production/artifacts/zy94m3ba_SDB_6734_Wien-in-Schwarz_002%20%282%29.jpg)'
            }}></div>
            <div className="about-content" data-reveal>
              <h2 className="section-title" data-testid="about-title">Wer hinter m<span className="ai-highlight-inline">AI</span>sblau steht</h2>
              <p className="about-text">
                Hinter mAIsblau steht <strong>Andreas Hofmarcher</strong> – Filmemacher und Journalist aus Wien. 
                Er kommt vom klassischen Dreh und aus der TV-Produktion und steuert heute die generativen 
                Modelle wie eine Kamera: mit einem Auge für Bild, Rhythmus und Geschichte. Das Ergebnis 
                sind KI-Videos mit echter Film-Handschrift statt Tool-Zufall.
              </p>
              <p className="about-text">
                Von KI-generierten Kurzvideos für Social Media über intime Interviews im Podcast-Style 
                bis hin zu großen TV-Produktionen – wir bringen deine Geschichte professionell auf den 
                Bildschirm. Egal ob AI Werbespot, Imagefilm oder Dokumentation – wir produzieren in 
                Wien und ganz Österreich.
              </p>
              <div className="about-features">
                <div className="feature-item">
                  <CheckCircle className="feature-icon" size={24} />
                  <span>KI-Technologie & Innovation</span>
                </div>
                <div className="feature-item">
                  <CheckCircle className="feature-icon" size={24} />
                  <span>Journalistischer Ansatz</span>
                </div>
                <div className="feature-item">
                  <CheckCircle className="feature-icon" size={24} />
                  <span>Authentische Geschichten</span>
                </div>
                <div className="feature-item">
                  <CheckCircle className="feature-icon" size={24} />
                  <span>Wien & 100km Umgebung</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section - SEO & AI Search Optimized */}
      <section className="faq-section" data-testid="faq-section" itemScope itemType="https://schema.org/FAQPage">
        <div className="container">
          <h2 className="section-title" data-testid="faq-title" data-reveal>
            <Sparkles className="inline mr-2" size={28} />
            Häufige Fragen zur AI Videoproduktion
          </h2>
          <p className="section-subtitle" data-reveal>Alles über KI-Videos, Kosten und Ablauf in Wien</p>
          
          <div className="faq-grid" data-reveal="stagger">
            {faqData.map((faq, idx) => (
              <div 
                key={idx} 
                className={`faq-item ${openFaq === idx ? 'faq-open' : ''}`}
                itemScope 
                itemProp="mainEntity" 
                itemType="https://schema.org/Question"
                data-testid={`faq-item-${idx}`}
              >
                <button 
                  className="faq-question"
                  onClick={() => setOpenFaq(openFaq === idx ? null : idx)}
                  aria-expanded={openFaq === idx}
                  data-testid={`faq-toggle-${idx}`}
                >
                  <span itemProp="name">{faq.q}</span>
                  <ChevronDown className={`faq-chevron ${openFaq === idx ? 'rotated' : ''}`} size={20} />
                </button>
                <div 
                  className="faq-answer"
                  itemScope 
                  itemProp="acceptedAnswer" 
                  itemType="https://schema.org/Answer"
                >
                  <p itemProp="text">{faq.a}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Regional SEO Section */}
      <section className="regions-section" data-testid="regions-section">
        <div className="container">
          <h2 className="section-title" data-testid="regions-title">
            <MapPin className="inline mr-2" size={28} />
            AI Videoproduktion in Wien & Umgebung
          </h2>
          <p className="section-subtitle">Wir sind in ganz Ostösterreich für Sie da</p>
          
          <div className="regions-content">
            <p className="regions-text" data-reveal>
              mAIsblau produziert innovative <strong>AI Videos</strong> und klassische Filmproduktionen in <strong>Wien</strong> und einem Radius von <strong>100 Kilometern</strong>. 
              Ob <strong>KI-generierter Werbespot</strong>, <strong>Imagefilm</strong> oder <strong>Dokumentation</strong> – wir kommen zu Ihnen.
            </p>
            <div className="regions-grid" data-reveal="stagger">
              <div className="region-group" data-testid="region-wien">
                <h3>Wien</h3>
                <p>Alle Bezirke: 1010-1230 Wien. Innere Stadt, Leopoldstadt, Landstraße, Wieden, Margareten, Mariahilf, Neubau und alle weiteren.</p>
              </div>
              <div className="region-group" data-testid="region-noe">
                <h3>Niederösterreich</h3>
                <p>St. Pölten, Wiener Neustadt, Baden, Mödling, Krems, Klosterneuburg, Tulln, Korneuburg, Stockerau, Schwechat, Perchtoldsdorf</p>
              </div>
              <div className="region-group" data-testid="region-bgld">
                <h3>Burgenland</h3>
                <p>Eisenstadt, Neusiedl am See, Oberpullendorf, Mattersburg und Umgebung</p>
              </div>
              <div className="region-group" data-testid="region-international">
                <h3>International</h3>
                <p>Bratislava (Slowakei) und grenznahe Regionen auf Anfrage</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Google Business & Maps Section */}
      <section className="google-section" data-testid="google-section">
        <div className="container">
          <h2 className="section-title" data-testid="google-section-title" data-reveal>
            <MapPin className="inline mr-2" size={28} />
            Unser Standort in Wien
          </h2>
          <p className="section-subtitle" data-reveal>Besuchen Sie uns oder starten Sie Ihr Projekt online</p>
          
          <div className="google-grid" data-reveal>
            <div className="google-map-wrapper" data-testid="google-map" ref={mapRef}>
              {mapVisible ? (
                <iframe
                  title="Maisblau Videoproduktion Wien - Google Maps"
                  src="https://www.google.com/maps?q=Maisblau+Videoproduktion+Wien&output=embed"
                  width="100%"
                  height="100%"
                  style={{ border: 0 }}
                  allowFullScreen
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                ></iframe>
              ) : (
                <div style={{ width: '100%', height: '100%', minHeight: '400px', background: '#1a1a1a', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'rgba(255,255,255,0.3)', fontSize: '0.9rem' }}>
                  <MapPin size={20} style={{ marginRight: 8 }} /> Karte wird geladen...
                </div>
              )}
            </div>
            
            <div className="google-info" data-testid="google-info">
              <div className="google-review-card">
                <div className="google-review-header">
                  <div className="google-logo-text">Google</div>
                  <div className="google-stars">
                    <Star size={18} fill="#FBBC04" stroke="#FBBC04" />
                    <Star size={18} fill="#FBBC04" stroke="#FBBC04" />
                    <Star size={18} fill="#FBBC04" stroke="#FBBC04" />
                    <Star size={18} fill="#FBBC04" stroke="#FBBC04" />
                    <Star size={18} fill="#FBBC04" stroke="#FBBC04" />
                  </div>
                </div>
                <h3>Maisblau Videoproduktion</h3>
                <p className="google-business-desc">
                  AI Videoproduktion & Filmproduktion in Wien. Story-getriebene Video-Porträts 
                  mit journalistischem Zugang.
                </p>
                <div className="google-business-details">
                  <div className="google-detail-item">
                    <MapPin size={16} />
                    <span>Wien, Österreich</span>
                  </div>
                  <div className="google-detail-item">
                    <Phone size={16} />
                    <span>+43 681 20179697</span>
                  </div>
                  <div className="google-detail-item">
                    <Mail size={16} />
                    <span>andreas@maisblau.video</span>
                  </div>
                </div>
              </div>
              
              <div className="google-cta-buttons">
                <a 
                  href="https://www.google.com/maps/search/Maisblau+Videoproduktion+Wien" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="google-btn google-btn-primary"
                  data-magnetic="true"
                  data-testid="google-review-btn"
                >
                  <Star size={18} />
                  Bewertung schreiben
                </a>
                <a 
                  href="https://www.google.com/maps/dir//Maisblau+Videoproduktion+Wien" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="google-btn google-btn-secondary"
                  data-magnetic="true"
                  data-testid="google-directions-btn"
                >
                  <ExternalLink size={18} />
                  Route planen
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Instagram Section */}
      <section className="instagram-section" data-testid="instagram-section">
        <div className="container">
          <div className="instagram-cta" data-reveal>
            <div className="instagram-text">
              <h3>Folge mir auf Instagram</h3>
              <p>Behind the Scenes, aktuelle Projekte & mehr aus Wien</p>
              <span className="instagram-handle">@maisblau_mrfetishaustria_2025</span>
            </div>
            <a 
              href="https://www.instagram.com/maisblau_mrfetishaustria_2025/" 
              target="_blank" 
              rel="noopener noreferrer"
              className="instagram-button"
              data-magnetic="true"
              data-testid="instagram-follow-button"
            >
              <Instagram size={24} />
              Folgen
            </a>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="contact-section" data-testid="contact-section">
        <div className="container">
          <h2 className="section-title" data-testid="contact-title" data-reveal>Jetzt Projekt starten</h2>
          <p className="section-subtitle" data-reveal>Erzählen Sie uns von Ihrem Projekt</p>
          
          <div className="contact-grid" data-reveal="stagger">
            <ContactForm />
            
            <div className="contact-info">
              <h3>Kontakt</h3>
              <div className="contact-detail" data-testid="contact-person">
                <strong>Andreas</strong>
                <p>Maisblau Videoproduktion</p>
              </div>
              <div className="contact-detail" data-testid="contact-phone-info">
                <Phone className="inline mr-2" size={18} />
                <a href="tel:+4368120179697">+43 681 20179697</a>
              </div>
              <div className="contact-detail" data-testid="contact-email-info">
                <Mail className="inline mr-2" size={18} />
                <a href="mailto:andreas@maisblau.video">andreas@maisblau.video</a>
              </div>
              <div className="social-links" data-testid="social-links">
                <a href="https://www.facebook.com/maisblau/" target="_blank" rel="noopener noreferrer" data-testid="facebook-link">
                  <Facebook size={24} />
                </a>
                <a href="https://www.instagram.com/maisblau_mrfetishaustria_2025" target="_blank" rel="noopener noreferrer" data-testid="instagram-link">
                  <Instagram size={24} />
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="footer" data-testid="footer">
        <div className="container">
          <div className="footer-content">
            <div className="footer-brand">
              <h3>M<span className="ai-highlight">AI</span>SBLAU</h3>
              <p>AI Videoproduktion & Filmproduktion Wien</p>
            </div>
            <div className="footer-center">
              <a 
                href="https://www.mrfetish.at" 
                target="_blank" 
                rel="noopener noreferrer"
                className="rainbow-button"
                data-testid="mrfetish-button"
              >
                🏳️‍🌈 Mr Fetish Austria
              </a>
            </div>
            <div className="footer-right">
              <div className="footer-links footer-links-services">
                <Link to="/ki-werbespot-wien" className="footer-link">KI-Werbespot</Link>
                <span className="footer-separator">|</span>
                <Link to="/ki-produktvideo-wien" className="footer-link">KI-Produktvideo</Link>
                <span className="footer-separator">|</span>
                <Link to="/ki-musikvideo-wien" className="footer-link">KI-Musikvideo</Link>
                <span className="footer-separator">|</span>
                <Link to="/ki-news" className="footer-link">KI-News</Link>
              </div>
              <div className="footer-links">
                <Link to="/impressum" className="footer-link">Impressum</Link>
                <span className="footer-separator">|</span>
                <Link to="/datenschutz" className="footer-link">Datenschutz</Link>
              </div>
              <div className="footer-copyright">
                <p>© 2025 Maisblau – Alle Rechte vorbehalten.</p>
              </div>
            </div>
          </div>
        </div>
      </footer>

      {/* Video Modal */}
      {selectedVideo && (
        <div className="video-modal" onClick={() => setSelectedVideo(null)} data-testid="video-modal">
          <div className="video-modal-content" onClick={(e) => e.stopPropagation()}>
            <button className="modal-close" onClick={() => setSelectedVideo(null)} data-testid="modal-close-button">×</button>
            <iframe
              width="100%"
              height="100%"
              src={selectedVideo.url}
              title={selectedVideo.title}
              frameBorder="0"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
              data-testid="video-iframe"
            ></iframe>
          </div>
        </div>
      )}

    </div>
  );
}

function ScrollToTop() {
  const { pathname, hash } = useLocation();
  useEffect(() => {
    if (!hash) window.scrollTo(0, 0);
  }, [pathname, hash]);
  return null;
}

function App() {
  return (
    <BrowserRouter>
      <div className="App" data-testid="maisblau-website">
        <ScrollToTop />
        <ScrollProgress />
        <CustomCursor />
        <Routes>
          <Route path="/" element={<HomePage />} />
          <Route path="/ki-werbespot-wien" element={<KiWerbespot />} />
          <Route path="/ki-produktvideo-wien" element={<KiProduktvideo />} />
          <Route path="/ki-musikvideo-wien" element={<KiMusikvideo />} />
          <Route path="/ki-news" element={<RadarList />} />
          <Route path="/ki-news/:slug" element={<RadarPost />} />
          <Route path="/radar-admin" element={<RadarAdmin />} />
          <Route path="/datenschutz" element={<Datenschutz />} />
          <Route path="/impressum" element={<Impressum />} />
        </Routes>
        <CookieBanner />
        <WhatsAppButton />
      </div>
    </BrowserRouter>
  );
}

export default App;